import React, { useMemo, useRef, useEffect } from 'react'
import AppDropdown from '../../../../components/AppDropdown'
import {
  StepHeader,
  StepCard,
  INPUT_CLASS,
  DROPDOWN_TRIGGER_CLASS,
  LABEL_CLASS,
} from '../components/StepLayout'

const CATEGORIES = [
  { value: 'General', label: 'General' },
  { value: 'Executive / Board', label: 'Executive / Board' },
  { value: 'Operational', label: 'Operational' },
  { value: 'Financial & Billing', label: 'Financial & Billing' },
  { value: 'Resource & Productivity', label: 'Resource & Productivity' },
  { value: 'Compliance & Audit', label: 'Compliance & Audit' },
]

const VISIBILITIES = [
  { id: 'PRIVATE', label: 'Private (Only Me)', icon: 'lock' },
  { id: 'SHARED', label: 'Shared with users', icon: 'group' },
  { id: 'TEAM', label: 'My Team', icon: 'diversity_3' },
  { id: 'DEPARTMENT', label: 'My Department', icon: 'domain' },
  { id: 'ORG', label: 'Organization-wide', icon: 'public' },
]

export default function Step1Setup({
  reportName,
  setReportName,
  description,
  setDescription,
  category,
  setCategory,
  companyId,
  setCompanyId,
  branchId,
  setBranchId,
  departmentId,
  setDepartmentId,
  projectId,
  setProjectId,
  visibility,
  setVisibility,
  companyOptions = [],
  branchOptions = [],
  departmentOptions = [],
  projectOptions = [],
}) {
  const normCompanies = useMemo(() => {
    if (!companyOptions.length) return [{ value: '', label: 'All Companies' }]
    if (companyOptions[0]?.value !== undefined) return companyOptions
    return [{ value: '', label: 'All Companies' }, ...companyOptions.map((c) => ({ value: String(c.id), label: c.name }))]
  }, [companyOptions])

  const normBranches = useMemo(() => {
    if (!branchOptions.length) return [{ value: '', label: 'All Branches' }]
    if (branchOptions[0]?.value !== undefined) return branchOptions
    return [{ value: '', label: 'All Branches' }, ...branchOptions.map((b) => ({ value: String(b.id), label: b.name }))]
  }, [branchOptions])

  const normDepts = useMemo(() => {
    if (!departmentOptions.length) return [{ value: '', label: 'All Departments' }]
    if (departmentOptions[0]?.value !== undefined) return departmentOptions
    return [{ value: '', label: 'All Departments' }, ...departmentOptions.map((d) => ({ value: String(d.id), label: d.name }))]
  }, [departmentOptions])

  const normProjects = useMemo(() => {
    if (!projectOptions.length) return [{ value: '', label: 'All Projects' }]
    if (projectOptions[0]?.value !== undefined) return projectOptions
    return [{ value: '', label: 'All Projects' }, ...projectOptions.map((p) => ({ value: String(p.id), label: p.name }))]
  }, [projectOptions])

  const textareaRef = useRef(null)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.max(40, textareaRef.current.scrollHeight)}px`
    }
  }, [description])

  return (
    <div className="space-y-3 max-w-4xl mx-auto py-1">
      <StepHeader
        title="Report Setup & Scoping"
        subtitle="Define core report identification, category, organizational boundary, and access visibility."
      />

      <StepCard>
        {/* Row 1: Report Name (2/3) + Category (1/3) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <div className="md:col-span-8">
            <label className={LABEL_CLASS}>
              Report Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              required
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="e.g., Monthly Project Margin & Resource Allocation"
              className={INPUT_CLASS}
            />
            {!reportName.trim() && (
              <p className="text-[10px] text-error font-medium mt-1">A report name is required to proceed.</p>
            )}
          </div>

          <div className="md:col-span-4">
            <label className={LABEL_CLASS}>
              Category
            </label>
            <AppDropdown
              value={category}
              onChange={setCategory}
              options={CATEGORIES}
              triggerClassName={DROPDOWN_TRIGGER_CLASS}
            />
          </div>
        </div>

        {/* Row 2: Description (Dynamically Auto-Expanding with 250 Character Limit) */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant block">
              Description
            </label>
            <span
              className={`text-[10px] font-medium transition-colors ${
                (description?.length || 0) >= 290
                  ? 'text-error font-bold'
                  : 'text-on-surface-variant/70'
              }`}
            >
              {(description || '').length}/300
            </span>
          </div>
          <textarea
            ref={textareaRef}
            rows={1}
            maxLength={300}
            value={description || ''}
            onChange={(e) => setDescription(e.target.value.slice(0, 300))}
            placeholder="Briefly state what intelligence or operational questions this report answers..."
            className="block min-h-[40px] w-full rounded-[8px] border border-surface-container-high bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all resize-none overflow-hidden"
          />
        </div>

        {/* Row 3: Scoping Filters in 4 Columns */}
        <div className="pt-2.5 border-t border-surface-container">
          <label className={LABEL_CLASS}>
            Organizational Boundary (Pre-Assignment)
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-on-surface-variant px-0.5">Company</span>
              <AppDropdown
                value={companyId}
                onChange={setCompanyId}
                options={normCompanies}
                triggerClassName={DROPDOWN_TRIGGER_CLASS}
              />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-on-surface-variant px-0.5">Branch</span>
              <AppDropdown
                value={branchId}
                onChange={setBranchId}
                options={normBranches}
                triggerClassName={DROPDOWN_TRIGGER_CLASS}
              />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-on-surface-variant px-0.5">Department</span>
              <AppDropdown
                value={departmentId}
                onChange={setDepartmentId}
                options={normDepts}
                triggerClassName={DROPDOWN_TRIGGER_CLASS}
              />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-on-surface-variant px-0.5">Project</span>
              <AppDropdown
                value={projectId}
                onChange={setProjectId}
                options={normProjects}
                triggerClassName={DROPDOWN_TRIGGER_CLASS}
              />
            </div>
          </div>
        </div>

        {/* Row 4: Report Visibility in 5 Columns */}
        <div className="pt-2.5 border-t border-surface-container">
          <label className={LABEL_CLASS}>
            Report Visibility & Access Policy
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {VISIBILITIES.map((v) => {
              const selected = visibility === v.id
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVisibility(v.id)}
                  className={`h-10 flex items-center gap-2 px-3 rounded-[8px] border text-left transition-all ${
                    selected
                      ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                      : 'border-surface-container-high bg-surface text-on-surface hover:bg-surface-container'
                  }`}
                >
                  <span className="material-symbols-outlined text-base shrink-0">{v.icon}</span>
                  <span className="text-xs truncate">{v.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      </StepCard>
    </div>
  )
}
